import { useState, useEffect } from "react";
import MaterialTable from "material-table";
import {
  Box,
  IconButton,
  Tooltip,
} from "@mui/material";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloudDownloadOutlinedIcon from "@mui/icons-material/CloudDownloadOutlined";
import SendIcon from "@mui/icons-material/Send";
import { toast } from "react-toastify";
import Popup from "../../../../../Base/views/Popup";
import ViewOffer from "../../../OfferLetter/ViewOffer";
import SendReshareOfferLetter from "../../SendReshareOfferLetter";
import { GetAtsJobKanbanOfferHelper } from "../../../../helper/AtsJobKanbanHelper";
import { DownloadAttachment } from "../../../../../Base/helper/baseFunctions";

const OfferPreparedGrid = ({ currentJobData, filters, sortValue }) => {
  const [elements, setElements] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openViewOffer, setOpenViewOffer] = useState(false);
  const [openReshare, setOpenReshare] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState(null);

  const getKanbanData = () => {
    if (!currentJobData?.ats_jobpost_id) return;
    const assigned_to_id =
      filters?.assignee_filter?.length > 0
        ? construcData(filters?.assignee_filter, "assigned_to_id", "recruiter_id")
        : null;
    const proposed_by_name =
      filters?.proposed_filter?.length > 0
        ? construcData(
            filters?.proposed_filter,
            "proposed_by_name",
            "proposed_by_name"
          )
        : null;
    const stage_id =
      filters?.stage_filter && filters.stage_filter.ats_job_stages_id
        ? JSON.stringify([{ stage_id: filters.stage_filter.ats_job_stages_id }])
        : null;

    setIsLoading(true);
    GetAtsJobKanbanOfferHelper({
      ats_jobpost_id: currentJobData?.ats_jobpost_id,
      assigned_to_id,
      proposed_by_name,
      ats_job_stages_id: stage_id,
      sort_order: sortValue?.order,
    })
      .then((res) => {
        if (res?.data?.statuscode === 200) {
          setElements(res?.data?.data || []);
        } else {
          setElements([]);
        }
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    getKanbanData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, sortValue, currentJobData?.ats_jobpost_id]);

  const handleViewOffer = (rowData) => {
    setSelectedCandidate(rowData);
    setOpenViewOffer(true);
  };

  const handleDownloadOffer = (rowData) => {
    if (rowData?.offer_letter_cloud_path) {
      DownloadAttachment(rowData.offer_letter_cloud_path, `${rowData?.first_name}_Offer_Letter.${rowData.offer_letter_cloud_path.split(".").at(-1)}`);
    } else if (
      rowData?.offer_letter_response_id ||
      rowData?.offer_preparation_completed
    ) {
      setSelectedCandidate(rowData);
      setOpenViewOffer(true);
    } else {
      toast.warning("Offer letter file not found");
    }
  };

  const handleReshare = (rowData) => {
    setSelectedCandidate(rowData);
    setOpenReshare(true);
  };

  const tableData = (elements || []).filter(
    (item) => (Boolean(item?.offer_letter_response_id)) && ["Offer Preparation", "Offer Released", "Offer Accepted", "Offer Rejected", "Joined"].includes(item?.job_stage_name));

  return (
    <Box sx={{ width: "100%" }}>
      <MaterialTable
        isLoading={isLoading}
        title=""
        columns={[
          {
            title: "Offer Letter ID",
            field: "offer_letter_no",
            cellStyle: { padding: "5px 10px 5px 13px" },
            headerStyle: { padding: "5px 10px 5px 13px", fontWeight: 600 },
            render: (rowData) => rowData?.offer_letter_no || "-",
          },
          {
            title: "Candidate Name",
            field: "full_name",
            cellStyle: { padding: "5px 10px 5px 13px" },
            headerStyle: { padding: "5px 10px 5px 13px", fontWeight: 600 },
          },
          {
            title: "Email",
            field: "email_id",
            sorting: false,
            cellStyle: { padding: "5px 10px 5px 13px" },
            headerStyle: { padding: "5px 10px 5px 13px", fontWeight: 600 },
            render: (rowData) => rowData?.email_id || "-"
          },
          {
            title: "Stage",
            field: "job_stage_name",
            sorting: false,
            cellStyle: { padding: "5px 10px 5px 13px" },
            headerStyle: { padding: "5px 10px 5px 13px", fontWeight: 600 },
            render: (rowData) => rowData?.job_stage_name || "-"
          },
          {
            title: "Offer",
            field: "offer",
            sorting: false,
            cellStyle: { padding: "5px 10px 5px 13px" },
            headerStyle: { padding: "5px 10px 5px 13px", fontWeight: 600 },
            render: (rowData) => {
              const isOfferCompleted =
                rowData?.offer_preparation_completed ||
                Boolean(rowData?.offer_letter_cloud_path) ||
                Boolean(rowData?.offer_letter_response_id);

              return (
                <Tooltip
                  title={
                    isOfferCompleted
                      ? "View Offer"
                      : "Offer not prepared yet"
                  }
                >
                  <span>
                    <IconButton
                      disabled={!isOfferCompleted}
                      onClick={() => handleViewOffer(rowData)}
                      sx={{
                        color: isOfferCompleted ? "#8b44ff" : "inherit",
                        "&:hover": { backgroundColor: "#f3edfd" },
                      }}
                    >
                      <VisibilityIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            },
          },
          {
            title: "Download Offer",
            field: "download_offer",
            sorting: false,
            cellStyle: { padding: "5px 10px 5px 16px" },
            headerStyle: { padding: "5px 10px 5px 5px", fontWeight: 600 },
            render: (rowData) => {
              const canDownload = Boolean(rowData?.offer_letter_cloud_path) && rowData?.offer_preparation_completed;
              return (
                <Tooltip
                  title={
                    canDownload
                      ? "Download Offer Letter"
                      : "Offer letter not available"
                  }
                >
                  <span>
                    <IconButton
                      disabled={!canDownload}
                      onClick={() => handleDownloadOffer(rowData)}
                      sx={{
                        color: canDownload ? "#8b44ff" : "inherit",
                        "&:hover": { backgroundColor: "#f3edfd" },
                      }}
                    >
                      <CloudDownloadOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            },
          },
          {
            title: "Send Offer",
            field: "send_offer",
            sorting: false,
            cellStyle: { padding: "5px 10px 5px 13px" },
            headerStyle: { padding: "5px 10px 5px 13px", fontWeight: 600 },
            render: (rowData) => {
              const isManagerApproved = rowData?.managers_approval;
              const isOfferCompleted = rowData?.offer_preparation_completed && Boolean(rowData?.offer_letter_cloud_path) && isManagerApproved;
              return (
                <Tooltip
                  title={
                    !isManagerApproved ? "Offer not approved" :
                    isOfferCompleted
                      ? "Send Offer Letter"
                      : "Offer preparation not completed"
                  }
                >
                  <span>
                    <IconButton
                      disabled={!isOfferCompleted && !isManagerApproved}
                      onClick={() => handleReshare(rowData)}  
                      sx={{
                        color: isOfferCompleted && isManagerApproved ? "#8b44ff" : "inherit",
                        "&:hover": { backgroundColor: "#f3edfd" },
                      }}                
                    >
                      <SendIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              );
            },
          },
        ]}
        data={tableData}
        options={{
          maxBodyHeight: "65vh",
          minBodyHeight: "65vh",
          rowStyle: { fontSize: "13px" },
          sorting: true,
          search: true,
          searchText: "",
          searchFieldAlignment: "right",
          searchFieldVariant: "standard",
          filtering: false,
          paging: true,
          pageSizeOptions: [10, 25, 50],
          pageSize: 25,
          paginationType: "stepped",
          showFirstLastPageButtons: false,
          paginationPosition: "bottom",
          exportButton: false,
          grouping: false,
          columnsButton: false,
        }}
      />

      <Popup
        fullWidth
        maxWidth="xl"
        title="Preview Offer Letter"
        isMasterProduct={false}
        setOpenPopup={setOpenViewOffer}
        openPopup={openViewOffer}
      >
        <ViewOffer data={selectedCandidate} />
      </Popup>

      <Popup
        fullWidth
        maxWidth="md"
        title="Send Offer Letter"
        openPopup={openReshare}
        setOpenPopup={setOpenReshare}
      >
        <SendReshareOfferLetter
          currentData={selectedCandidate}
          setOpenPopup={setOpenReshare}
          currentJobData={currentJobData}
          setElements={setElements}
        />
      </Popup>
    </Box>
  );
};

const construcData = (value, label, key) => {
  var temp = [];
  value?.forEach((res) => {
    temp.push({ [label]: res[key] });
  });
  return JSON.stringify(temp);
};

export default OfferPreparedGrid;
